// Custom select element

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
} from '@floating-ui/react';
import ChevronUpDown from './ChevronUpDown';
import GetClickOutside from './GetClickOutside';
import FilterInput from './FilterInput';
import './Select.scss';

export default function Select({
  options,
  value,
  onChange,
  small,
  className,
  placeholder,
  error,
  search,
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(''); // Local query for filtering options
  const listRef = useRef(null); // Ref to options list for size middleware
  const ignoreNextClickRef = useRef(false); // Skip field click after outside click

  // Compute filtered options (case-insensitive)
  const filteredOptions = useMemo(() => {
    if (!search || !query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, query, search]);

  // Setup Floating UI to position dropdown within viewport and ignore parent overflow
  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed', // Use fixed to escape clipping/scroll parents
    middleware: [
      offset(8), // Gap between trigger and dropdown
      flip({ fallbackPlacements: ['top-start', 'top-end'] }), // Flip if not enough space
      shift({ padding: 8 }), // Nudge to keep within viewport
      size({
        padding: 8,
        // Dynamically control width/height based on available space
        apply({ rects, elements, availableHeight }) {
          // Match dropdown min width to trigger width
          Object.assign(elements.floating.style, {
            minWidth: `${Math.round(rects.reference.width)}px`,
            maxWidth: 'min(92vw, 640px)',
          });
          // Compute max height for list excluding search row height
          const searchRow = elements.floating.querySelector('.search-row');
          const headerH = searchRow ? searchRow.offsetHeight : 0;
          const maxListH = Math.max(availableHeight - headerH - 2, 64);
          if (listRef.current) {
            Object.assign(listRef.current.style, {
              maxHeight: `${Math.floor(maxListH)}px`,
              overflowY: 'auto',
            });
          }
        },
      }),
    ],
  });

  // Keep floating position in sync when dropdown visibility changes
  useEffect(() => {
    if (isOpen) update?.();
  }, [isOpen, update, filteredOptions.length, query]);

  // Clear query whenever dropdown closes or value is chosen
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  // Close dropdown if control becomes disabled
  useEffect(() => {
    if (disabled && isOpen) setIsOpen(false);
  }, [disabled, isOpen]);

  return (
    <div
      className={
        'select' + (small ? ' small' : '') + (className ? ' ' + className : '')
      }
    >
      <div
        ref={refs.setReference} // Bind trigger element as reference for Floating UI
        className={
          'selected-option' +
          (error ? ' error' : '') +
          (disabled ? ' disabled' : '')
        }
        onClick={() => {
          if (disabled) return;
          if (ignoreNextClickRef.current) return; // Ignore click that already closed dropdown
          setIsOpen((v) => !v);
        }}
        role='button'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-disabled={disabled ? 'true' : 'false'}
        // Names the field even once a value hides the placeholder text
        aria-label={
          placeholder && value ? `${placeholder}: ${value}` : placeholder
        }
      >
        <p className={'value' + (value ? '' : ' placeholder')}>
          {value || placeholder}
        </p>
        <ChevronUpDown up={isOpen} />
        <p className={'error-msg' + (error ? ' visible' : '')}>{error}</p>
        <p className={'label' + (value ? ' visible' : '')}>{placeholder}</p>
      </div>
      <FloatingPortal>
        <GetClickOutside
          active={isOpen}
          onClick={() => {
            setIsOpen(false);
            ignoreNextClickRef.current = true; // Mark that this event already closed dropdown
            setTimeout(() => {
              ignoreNextClickRef.current = false; // Reset flag for future clicks
            }, 300);
          }}
        >
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={
              'select-floating' +
              (isOpen ? ' open' : '') +
              (small ? ' small' : '')
            }
            role='listbox'
            aria-label={placeholder || 'Select'}
          >
            <ul className='options' ref={listRef}>
              {search && (
                <li className='search-row' onClick={(e) => e.stopPropagation()}>
                  <FilterInput value={query} onChange={setQuery} small />
                </li>
              )}
              {filteredOptions.map((option, index) => (
                <li
                  key={index}
                  role='option'
                  aria-selected={String(option) === String(value)}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                    setQuery('');
                  }}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    // Support keyboard selection by Enter/Space
                    if (e.key === 'Enter' || e.key === ' ') {
                      onChange(option);
                      setIsOpen(false);
                      setQuery('');
                    }
                  }}
                >
                  {option}
                </li>
              ))}
            </ul>
          </div>
        </GetClickOutside>
      </FloatingPortal>
    </div>
  );
}
